import { useEffect, useState } from "react";
import CSDTable from "@cloudscape-design/components-themed/table";
import {
  Box,
  Icon,
  Button,
  Header,
  SpaceBetween,
} from "@cloudscape-design/components-themed";
import { DEFAULT_PAGE_SIZE } from "../../configs/constants";
import { getHeaderCounterText, getPagedItems } from "../../utils/common";
import {
  COMMON_LABELS,
  getStatus,
  ApprovalStatusPattern,
  getDivisionItems,
  getDivisionLabel,
} from "../default-constants";
import Preference from "../UI/Preference";
import Pagination from "../UI/Pagination";
import { LABELS, getFlagDisplayText } from "./constants";
import {
  useGetMerchantTypesQuery,
  useGetDivisionQuery,
} from "../../features/api";
import { useNavigate, Link } from "react-router-dom";
import { MerchantTypeReference, Division } from "../../features/api-interfaces";

const columnDefinitions = [
  {
    id: "approvalStatus",
    header: LABELS.APPROVAL_STATUS,
    cell: (e: any) =>
      e.approvalStatusName ===
      ApprovalStatusPattern.awaitingApproval.statusName ? (
        <SpaceBetween direction="horizontal" size="xxxs">
          <Icon name="flag" variant="error" />
          {e.approvalStatusName}
        </SpaceBetween>
      ) : (
        e.approvalStatusName
      ),
  },
  {
    id: "typeName",
    header: LABELS.TYPE_NAME,
    cell: (e: any) =>
      e.approvalStatusName !== "" ? (
        <Link to={"/merchant-types/" + e.merchantTypeNo}>{e.typeName}</Link>
      ) : (
        e.typeName
      ),
  },
  {
    id: "addressManagement",
    header: LABELS.ADDRESS_MANAGEMENT_FLAG,
    cell: (e: any) => e.addressManagement,
  },
  {
    id: "storeInfoAggregationTarget",
    header: LABELS.STORE_INFORMATION_AGGREGATION_TARGET_FLAG,
    cell: (e: any) => e.storeInfoAggregationTarget,
  },
  {
    id: "fakeIndustryCheck",
    header: LABELS.FAKE_INDUSTRY_CHECK_FLAG,
    cell: (e: any) => e.fakeIndustryCheck,
  },
  {
    id: "storeApplicationForm",
    header: LABELS.STORE_APPLICATION_FORM,
    cell: (e: any) => e.storeApplicationForm,
  },
  {
    id: "tenantExamination",
    header: LABELS.TENANT_EXAMINATION_FLAG,
    cell: (e: any) => e.tenantExamination,
  },
  {
    id: "tenantExaminationUnit",
    header: LABELS.TENANT_EXAMINATION_UNIT,
    cell: (e: any) => e.tenantExaminationUnit,
  },
  {
    id: "applicationStartDate",
    header: LABELS.APPLICABLE_START_DATE,
    cell: (e: any) => e.applicationStartDate,
  },
];

/*
加盟店種別一覧
 */
const Table = () => {
  let { data, isFetching, refetch } = useGetMerchantTypesQuery();
  const navigator = useNavigate();

  // 区分取得API呼び出し(親区分コード：1:店舗申請様式)
  const divisionData1 = useGetDivisionQuery("1");
  const storeApplicationFormDivisions = divisionData1?.data as Division[];
  const storeApplicationFormKbnItems = getDivisionItems(
    storeApplicationFormDivisions
  );

  // 区分取得API呼び出し(親区分コード：2:店子審査単位)
  const divisionData2 = useGetDivisionQuery("2");
  const tenantExaminationUnitDivisions = divisionData2?.data as Division[];
  const tenantExaminationUnitKbnItems = getDivisionItems(
    tenantExaminationUnitDivisions
  );

  const updateData = data?.map((item) => ({
    ...item,
    approvalStatusName: getStatus(item.approvalStatus),
    addressManagement: getFlagDisplayText(
      item.addressManagementRequiredFlag,
      "isRequired"
    ),
    storeInfoAggregationTarget: getFlagDisplayText(
      item.storeInfoAggregationTargetFlag,
      "isTarget"
    ),
    fakeIndustryCheck: getFlagDisplayText(
      item.fakeIndustryCheckRequiredFlag,
      "isRequired"
    ),
    tenantExamination: getFlagDisplayText(
      item.tenantExaminationRequiredFlag,
      "isRequired"
    ),
    storeApplicationForm: getDivisionLabel(
      storeApplicationFormKbnItems,
      item.storeApplicationFormKbn
    ),
    tenantExaminationUnit: getDivisionLabel(
      tenantExaminationUnitKbnItems,
      item.tenantExaminationUnitKbn
    ),
  })) as MerchantTypeReference[];

  // 新規登録ボタン押下時イベント
  const registOnClick = () => {
    // 新規登録画面に遷移
    navigator("/merchant-types/regist");
  };

  useEffect(() => {
    refetch();
  }, [refetch]);

  /**ページャ関連 */
  const options = columnDefinitions.map((column) => ({
    id: column.id,
    label: column.header,
  }));
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [preferences, setPreferences] = useState({
    pageSize: DEFAULT_PAGE_SIZE,
    visibleContent: options.map((o) => o.id),
  });

  const [pagedItems, setPagedItems] = useState<Object[]>([]);
  const [allItems, setAllItems] = useState<Object[]>([]);
  useEffect(() => {
    if (updateData !== undefined) {
      setPagedItems(getPagedItems(updateData, 1, preferences.pageSize));
      setAllItems(updateData);
      setCurrentPageIndex(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  /** 設定変更関数 */
  const handleOnConfirm = (detail: any) => {
    const newPageSize = detail.pageSize;
    setPreferences({
      pageSize: newPageSize,
      visibleContent: detail.visibleContent,
    });
    setPagedItems(updateData.slice(0, newPageSize));
    setCurrentPageIndex(1);
  };

  return (
    <CSDTable
      columnDefinitions={columnDefinitions}
      loading={isFetching}
      loadingText={COMMON_LABELS.CSD.LOADING_TEXT}
      trackBy="typeNo"
      items={pagedItems}
      resizableColumns
      stickyHeader
      header={
        <SpaceBetween size="xs">
          <Button variant="normal" iconAlign="right" onClick={registOnClick}>
            {LABELS.MERCHANT_TYPE_REGISTRATION}
          </Button>
          <Header>
            {LABELS.TABLE_TITLE}
            {getHeaderCounterText(allItems.length, pagedItems.length)}
          </Header>
        </SpaceBetween>
      }
      pagination={
        <Pagination
          currentPageIndex={currentPageIndex}
          filteredItems={allItems}
          setCurrentPageIndex={setCurrentPageIndex}
          pageSize={preferences.pageSize}
          setPagedItems={setPagedItems}
        />
      }
      preferences={
        <Preference
          preferences={preferences}
          options={options}
          handleOnConfirm={handleOnConfirm}
        />
      }
      empty={
        <Box textAlign="center" color="inherit">
          {COMMON_LABELS.CSD.NO_DATA}
        </Box>
      }
    />
  );
};

export default Table;
